import React, { useState, useEffect, ChangeEvent } from 'react';
import { PlusIcon, PencilSquareIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { api } from '../../services/api';
import { FormField, Input, TextArea } from '../../components/common/FormField';
import { Modal } from '../../components/common/Modal';

interface CardTemplate {
  id: string;
  name: string;
  description: string;
  frontLayout: string;
  backLayout: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CardTemplateFormData {
  name: string;
  description: string;
  frontLayout: string;
  backLayout: string;
  isActive: boolean;
}

const CardTemplateFormContent: React.FC<{
  formData: CardTemplateFormData;
  setFormData: React.Dispatch<React.SetStateAction<CardTemplateFormData>>;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  editingTemplate: CardTemplate | null;
  onClose: () => void;
}> = ({ formData, setFormData, handleSubmit, editingTemplate, onClose }) => {
  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="mt-4 space-y-6">
      <FormField label="Name" required>
        <Input
          type="text"
          value={formData.name}
          onChange={handleChange('name')}
          required
        />
      </FormField>

      <FormField label="Description" required>
        <TextArea
          value={formData.description}
          onChange={handleChange('description')}
          required
          rows={3}
        />
      </FormField>

      <div>
        <FormField label="Front Layout" required>
          <TextArea
            value={formData.frontLayout}
            onChange={handleChange('frontLayout')}
            required
            rows={5}
          />
          <p className="mt-1 text-sm text-gray-500">Enter the HTML/CSS template for the front of the card</p>
        </FormField>
      </div>

      <div>
        <FormField label="Back Layout" required>
          <TextArea
            value={formData.backLayout}
            onChange={handleChange('backLayout')}
            required
            rows={5}
          />
          <p className="mt-1 text-sm text-gray-500">Enter the HTML/CSS template for the back of the card</p>
        </FormField>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={formData.isActive}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, isActive: e.target.checked })}
          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
        />
        <label className="text-sm text-gray-900">
          Active
        </label>
        <p className="text-sm text-gray-500 ml-2">Enable this template for use in trading cards</p>
      </div>

      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-300 bg-black/30 border border-teal-500/20 rounded-md hover:bg-black/40 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-teal-500 border border-transparent rounded-md hover:bg-teal-600 transition-colors"
        >
          {editingTemplate ? 'Save Changes' : 'Create Template'}
        </button>
      </div>
    </form>
  );
};

export const TradingCardConfigPage: React.FC = () => {
  const [templates, setTemplates] = useState<CardTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<CardTemplate | null>(null);
  const [formData, setFormData] = useState<CardTemplateFormData>({
    name: '',
    description: '',
    frontLayout: '',
    backLayout: '',
    isActive: true,
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const data = await api.admin.getTradingCards();
      setTemplates(data);
    } catch (err) {
      setError('Failed to load card templates');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingTemplate) {
        await api.admin.updateTradingCard(editingTemplate.id, formData);
      } else {
        await api.admin.createTradingCard(formData);
      }
      await fetchTemplates();
      setIsModalOpen(false);
      resetForm();
    } catch (err) {
      console.error('Failed to save template:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      await api.admin.deleteTradingCard(id);
      await fetchTemplates();
    } catch (err) {
      console.error('Failed to delete template:', err);
    }
  };

  const openEditModal = (template: CardTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description,
      frontLayout: template.frontLayout,
      backLayout: template.backLayout,
      isActive: template.isActive,
    });
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      description: '',
      frontLayout: '',
      backLayout: '',
      isActive: true,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 text-center py-8">
        <p>{error}</p>
        <button
          onClick={fetchTemplates}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Trading Card Templates</h1>
        <button
          onClick={() => {
            resetForm();
            setIsModalOpen(true);
          }}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Add Template
        </button>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Template Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Updated
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {templates.map((template) => (
              <tr key={template.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{template.name}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-500">{template.description}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    template.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {template.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(template.updatedAt).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => openEditModal(template)}
                    className="text-blue-600 hover:text-blue-900 mr-4"
                  >
                    <PencilSquareIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(template.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingTemplate ? 'Edit Template' : 'Add Template'}
      >
        <CardTemplateFormContent
          formData={formData}
          setFormData={setFormData}
          handleSubmit={handleSubmit}
          editingTemplate={editingTemplate}
          onClose={() => setIsModalOpen(false)}
        />
      </Modal>
    </div>
  );
}; 